<template>
  <div class="onboarding-container">
    <!-- Floating Help Button -->
    <VaButton class="help-button" icon="help_outline" color="primary" round size="large" @click="toggleMenu" />

    <!-- Help Menu Dropdown -->
    <transition name="slide-up">
      <div v-if="showMenu" class="help-menu">
        <div class="help-menu-header">
          <VaIcon name="school" size="20px" />
          <span>Product Walkthroughs</span>
        </div>

        <div class="help-menu-content">
          <button v-if="currentTour" class="tour-item current-tour" @click="startCurrentTour">
            <VaIcon name="play_circle" color="primary" />
            <div class="tour-info">
              <span class="tour-name">{{ currentTour.name }}</span>
              <span class="tour-desc">{{ currentTour.description || 'Start page walkthrough' }}</span>
            </div>
            <VaIcon v-if="isTourCompleted" name="check_circle" color="success" size="16px" />
          </button>

          <div class="divider" v-if="currentTour"></div>

          <div class="menu-section">
            <span class="section-title">Quick Help</span>
            <button class="help-item" @click="openDocumentation">
              <VaIcon name="menu_book" />
              <span>Documentation</span>
            </button>
            <button class="help-item" @click="resetProgress">
              <VaIcon name="refresh" />
              <span>Reset All Tours</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Overlay to close menu -->
    <div v-if="showMenu" class="help-overlay" @click="showMenu = false"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vuestic-ui'
import { useOnboarding } from '@/composables/useOnboarding'
import { tourRegistry } from '@/config/tours'

const route = useRoute()
const { init } = useToast()
const { startTour, isTourCompleted: checkTourCompleted, resetTourProgress } = useOnboarding()

const showMenu = ref(false)

// Get current page tour ID from route
const currentTourId = computed(() => {
  const path = route.path.split('/').filter(Boolean)[0] || 'dashboard'
  return path
})

// Get current tour config
const currentTour = computed(() => {
  return tourRegistry[currentTourId.value] || null
})

// Check if current tour is completed
const isTourCompleted = computed(() => {
  return checkTourCompleted(currentTourId.value)
})

// Toggle menu
const toggleMenu = () => {
  showMenu.value = !showMenu.value
}

// Start the current page tour
const startCurrentTour = () => {
  if (currentTour.value) {
    showMenu.value = false
    setTimeout(() => {
      startTour(currentTourId.value, currentTour.value.steps)
    }, 200)
  }
}

// Open documentation
const openDocumentation = () => {
  window.open('https://docs.hris.com', '_blank')
  showMenu.value = false
}

// Reset all tour progress
const resetProgress = () => {
  resetTourProgress()
  init({ message: 'All tour progress has been reset', color: 'info' })
  showMenu.value = false
}

// Close menu on escape
const handleEscape = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    showMenu.value = false
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleEscape)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape)
})
</script>

<style scoped>
.onboarding-container {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
}

.help-button {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition:
    transform 0.2s,
    box-shadow 0.2s;
}

.help-button:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.help-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
}

.help-menu {
  position: absolute;
  bottom: 60px;
  right: 0;
  width: 280px;
  background: var(--va-background-primary);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.help-menu-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: linear-gradient(135deg, var(--va-primary), var(--va-info));
  color: white;
  font-weight: 600;
}

.help-menu-content {
  padding: 8px;
}

.tour-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px;
  border: none;
  background: var(--va-background-element);
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: background 0.2s;
}

.tour-item:hover {
  background: var(--va-background-border);
}

.tour-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.tour-name {
  font-weight: 600;
  font-size: 14px;
  color: var(--va-text-primary);
}

.tour-desc {
  font-size: 12px;
  color: var(--va-text-secondary);
}

.divider {
  height: 1px;
  background: var(--va-background-border);
  margin: 8px 0;
}

.menu-section {
  padding: 8px 0;
}

.section-title {
  display: block;
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--va-text-secondary);
}

.help-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: var(--va-text-primary);
  transition: background 0.2s;
}

.help-item:hover {
  background: var(--va-background-element);
}

/* Slide up animation */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.2s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Dark mode support */
:root[data-theme='dark'] .help-menu {
  border: 1px solid rgba(255, 255, 255, 0.1);
}
</style>
